import { useEffect, Dispatch, SetStateAction } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddTagFormData, SampleTagResponse } from '../types';
import { transformApiResponseToFormData } from '../utils/transformers';

interface UseFormInitializationProps {
  isAddTagsOpened: boolean;
  editMode: boolean;
  duplicateMode: boolean;
  isCreateNewMode: boolean;
  isSubmitted: boolean;
  sourceTagData?: SampleTagResponse;
  methods: UseFormReturn<AddTagFormData>;
  setDrawerState: Dispatch<
    SetStateAction<{
      error: string | null;
      isSubmitted: boolean;
      initialFormData: AddTagFormData | null;
      isCreateNewMode: boolean;
      createdTagData: SampleTagResponse | undefined;
      isDownloading: boolean;
      isSubmitting: boolean;
    }>
  >;
}

/**
 * Initialize form for editing existing tag
 */
function initializeForEdit(
  sourceTagData: SampleTagResponse,
  reset: UseFormReturn<AddTagFormData>['reset'],
  setDrawerState: UseFormInitializationProps['setDrawerState'],
) {
  const formData = transformApiResponseToFormData(sourceTagData);
  reset(formData);
  setDrawerState((prev) => ({ ...prev, initialFormData: formData }));
}

/**
 * Initialize form for duplicating existing tag
 */
function initializeForDuplicate(
  sourceTagData: SampleTagResponse,
  reset: UseFormReturn<AddTagFormData>['reset'],
  setDrawerState: UseFormInitializationProps['setDrawerState'],
) {
  const formData = transformApiResponseToFormData(sourceTagData);

  const duplicateFormData = {
    ...formData,
    id: undefined,
  };

  reset(duplicateFormData);
  setDrawerState((prev) => ({ ...prev, initialFormData: null }));
}

/**
 * Custom hook to handle form initialization logic
 * Centralizes all the complex form reset logic in one place
 */
export function useFormInitialization({
  isAddTagsOpened,
  editMode,
  duplicateMode,
  isCreateNewMode,
  isSubmitted,
  sourceTagData,
  methods,
  setDrawerState,
}: UseFormInitializationProps) {
  useEffect(() => {
    if (!isAddTagsOpened || isSubmitted || isCreateNewMode) {
      return;
    }

    setDrawerState((prev) => ({
      ...prev,
      isSubmitted: false,
      error: null,
      isCreateNewMode: false,
    }));

    if (editMode && sourceTagData) {
      initializeForEdit(sourceTagData, methods.reset, setDrawerState);
    } else if (duplicateMode && sourceTagData) {
      initializeForDuplicate(sourceTagData, methods.reset, setDrawerState);
    }
  }, [
    isAddTagsOpened,
    editMode,
    duplicateMode,
    sourceTagData,
    isSubmitted,
    isCreateNewMode,
    methods.reset,
    methods.getValues,
    setDrawerState,
  ]);
}
